import { useCallback, useEffect, useRef, useState } from "react";
import { View } from "react-native";
import ShareMenu, { SharePayloadProp } from "src/components/ShareMenu";
import useCelebration from "src/hooks/useCelebration";
import useResolveMaybePromise from "src/hooks/useResolveMaybePromise";
import useStyle from "src/hooks/useStyle";
import { useInsideModal } from "src/modals";
import GlobalModal, { ShareModalProps } from "src/modals/configs/GlobalModal";
import { color } from "src/styles";
import { radiusMedium } from "src/styles/radius";
import { paddingLarge, paddingMedium, paddingXXXL } from "src/styles/spacing";
import { Text } from "src/swsh-native";
import { Timeout } from "src/types/time";
import ShareModalPreview from "./ShareModalPreview";

const ShareModalContent = ({
	header,
	data,
	onComplete,
	preview,
}: Pick<ShareModalProps, "header" | "onComplete" | "preview"> & {
	data: SharePayloadProp["data"];
}) => {
	const { title, subTitle } = header;
	const { theme } = useStyle();
	const { launchConfettiCannon } = useCelebration();
	const { closeSelf } = useInsideModal<GlobalModal["Share"]>();

	const handleComplete = useCallback(() => {
		closeSelf();
		onComplete?.();
	}, [closeSelf, onComplete]);
	const [isCopied, setIsCopied] = useState(false);
	const firstShare = data[0];
	const timeout = useRef<Timeout | null>(null);
	useEffect(() => {
		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	const [previewDim, setPreviewDim] = useState<{
		width: number;
		height: number;
	} | null>(null);
	const resolvedPreview = useResolveMaybePromise(preview);

	return (
		<>
			<Text
				testID="ShareModalTitle"
				style={{
					marginHorizontal: paddingLarge,
					alignSelf: "center",
					textAlign: "center",
				}}
				xl
				bold
			>
				{title}
			</Text>
			{!!subTitle && (
				<Text
					testID="ShareModalSubTitle"
					style={{
						marginHorizontal: paddingLarge,
						alignSelf: "center",
						textAlign: "center",
					}}
					color="TextLight"
				>
					{subTitle}
				</Text>
			)}
			<View
				pointerEvents="box-none"
				style={{
					flex: 1,
					justifyContent: "center",
					alignItems: "center",
					marginHorizontal: paddingXXXL,
				}}
				onLayout={(event) => {
					const { width, height } = event.nativeEvent.layout;
					setPreviewDim({ width, height });
				}}
			>
				{firstShare && firstShare.type === "Text" ? (
					<View
						style={{
							marginTop: paddingMedium,
							marginBottom: paddingXXXL,
							marginHorizontal: paddingLarge,
							padding: paddingLarge,
							borderRadius: radiusMedium,
							borderWidth: 1,
							borderColor: color.Border[theme],
							borderStyle: isCopied ? "solid" : "dashed",
						}}
					>
						<Text selectable>{firstShare.text}</Text>
					</View>
				) : !!previewDim && resolvedPreview !== null ? (
					<ShareModalPreview
						width={previewDim.width}
						height={previewDim.height}
						preview={resolvedPreview}
					/>
				) : (
					<></>
				)}
			</View>

			<View>
				<ShareMenu
					data={data}
					onComplete={() => {
						timeout.current = setTimeout(() => {
							handleComplete();
						}, 2000);
					}}
					onSuccess={() => {
						launchConfettiCannon("TrifectaButFromTheTop");
					}}
					onCopy={() => setIsCopied(true)}
					onCopyClear={() => setIsCopied(false)}
				/>
			</View>
		</>
	);
};
export default ShareModalContent;
